import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { 
  MessageSquare, Send, X, AlertCircle, CheckCircle, 
  Clock, ArrowLeft, Filter, Inbox, MessageCircle,
  CheckSquare, XSquare, Settings, AlertTriangle
} from 'lucide-react';
import LoadingSpinner from '../common/LoadingSpinner';
import { decodeHtmlEntities } from '../../utils/textUtils';

/**
 * Pannello gestione feedback per Admin
 * Permette agli admin di:
 * - Visualizzare tutti i feedback delle gym
 * - Rispondere ai feedback
 * - Cambiare status e priorità
 * - Filtrare per status, priorità, gym
 */
export default function FeedbackManager() {
  const { getAuthenticatedApi } = useAuth();
  
  // State principale
  const [feedbacks, setFeedbacks] = useState([]);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingResponses, setLoadingResponses] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // State risposta
  const [replyMessage, setReplyMessage] = useState('');
  
  // State filtri
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [showOnlyUnread, setShowOnlyUnread] = useState(false);

  useEffect(() => {
    loadFeedbacks();
  }, []);

  // Carica tutti i feedback
  const loadFeedbacks = async () => {
    try {
      setLoading(true);
      const api = getAuthenticatedApi();
      const response = await api.get('/feedback_api.php');
      
      if (response.data.success) {
        setFeedbacks(response.data.feedbacks || []);
      }
    } catch (err) {
      console.error('Errore caricamento feedback:', err);
      setError('Errore nel caricamento dei feedback');
    } finally {
      setLoading(false);
    }
  };

  // Carica conversazione
  const loadConversation = async (feedbackId) => {
    try {
      setLoadingResponses(true);
      const api = getAuthenticatedApi();
      const response = await api.get(`/feedback_responses_api.php?feedback_id=${feedbackId}`);
      
      if (response.data.success) {
        setResponses(response.data.responses || []);
      }
    } catch (err) {
      console.error('Errore caricamento conversazione:', err);
      setError('Errore nel caricamento della conversazione');
    } finally {
      setLoadingResponses(false);
    }
  };

  // Invia risposta
  const handleSendReply = async () => {
    if (!replyMessage.trim()) {
      setError('Scrivi un messaggio prima di inviare');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      const api = getAuthenticatedApi();
      const response = await api.post('/feedback_responses_api.php', {
        feedback_id: selectedFeedback.id,
        message: replyMessage
      });
      
      if (response.data.success) {
        setReplyMessage('');
        setSuccess('Risposta inviata! La palestra riceverà una notifica.');
        
        // Ricarica conversazione
        await loadConversation(selectedFeedback.id);
        
        // Ricarica lista
        await loadFeedbacks();
        
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      console.error('Errore invio risposta:', err);
      setError('Errore nell\'invio della risposta');
    } finally {
      setLoading(false);
    }
  };

  // Cambia status
  const handleChangeStatus = async (newStatus) => {
    try {
      setLoading(true);
      const api = getAuthenticatedApi();
      
      const response = await api.put('/feedback_responses_api.php', {
        feedback_id: selectedFeedback.id,
        status: newStatus
      });
      
      if (response.data.success) {
        setSuccess(`Status cambiato in: ${newStatus}`);
        
        // Aggiorna feedback locale
        setSelectedFeedback({ ...selectedFeedback, status: newStatus });
        
        // Ricarica lista
        await loadFeedbacks();
        
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      console.error('Errore cambio status:', err);
      setError('Errore nel cambio di status');
    } finally {
      setLoading(false);
    }
  };

  // Cambia severity
  const handleChangeSeverity = async (newSeverity) => {
    try {
      setLoading(true);
      const api = getAuthenticatedApi();
      
      const response = await api.put('/feedback_responses_api.php', {
        feedback_id: selectedFeedback.id,
        severity: newSeverity
      });
      
      if (response.data.success) {
        setSuccess(`Priorità cambiata in: ${newSeverity}`);
        
        // Aggiorna feedback locale
        setSelectedFeedback({ ...selectedFeedback, severity: newSeverity });
        
        // Ricarica lista
        await loadFeedbacks();
        
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      console.error('Errore cambio priorità:', err);
      setError('Errore nel cambio di priorità');
    } finally {
      setLoading(false);
    }
  };

  // Apri feedback
  const handleOpenFeedback = (feedback) => {
    setSelectedFeedback(feedback);
    loadConversation(feedback.id);
  };

  // Chiudi feedback
  const handleCloseFeedback = () => {
    setSelectedFeedback(null);
    setResponses([]);
    setReplyMessage('');
  };

  // Filtra feedback
  const filteredFeedbacks = feedbacks.filter(f => {
    if (filterStatus !== 'all' && f.status !== filterStatus) return false;
    if (filterSeverity !== 'all' && f.severity !== filterSeverity) return false;
    if (showOnlyUnread && !f.unread_by_admin) return false;
    return true;
  });

  // Contatori
  const counts = {
    total: feedbacks.length,
    new: feedbacks.filter(f => f.status === 'new').length,
    in_progress: feedbacks.filter(f => f.status === 'in_progress').length,
    unread: feedbacks.filter(f => f.unread_by_admin).length
  };

  // Badge helpers
  const getStatusBadge = (status) => {
    const badges = {
      'new': { color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300', label: 'Nuovo' },
      'in_progress': { color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300', label: 'In Lavorazione' },
      'closed': { color: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300', label: 'Chiuso' },
      'rejected': { color: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300', label: 'Rifiutato' }
    };
    return badges[status] || badges['new'];
  };

  const getSeverityBadge = (severity) => {
    const badges = {
      'low': { color: 'bg-gray-100 text-gray-800', label: 'Bassa', icon: '🟢' },
      'medium': { color: 'bg-blue-100 text-blue-800', label: 'Media', icon: '🟡' },
      'high': { color: 'bg-orange-100 text-orange-800', label: 'Alta', icon: '🟠' },
      'critical': { color: 'bg-red-100 text-red-800', label: 'Critica', icon: '🔴' }
    };
    return badges[severity] || badges['medium'];
  };

  // Vista conversazione
  if (selectedFeedback) {
    return (
      <div className="h-screen flex flex-col bg-gray-50 dark:bg-gray-900 overflow-hidden">
        {/* Header conversazione */}
        <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-4 sm:p-6 flex-shrink-0">
          <button
            onClick={handleCloseFeedback}
            className="mb-4 flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">Torna alla lista</span>
          </button>
          
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-4 gap-4">
            <div className="flex-1 min-w-0">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-2 break-words">
                {decodeHtmlEntities(selectedFeedback.title)}
              </h2>
              <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-3">
                <span className={`px-2 sm:px-3 py-1 text-xs sm:text-sm font-medium rounded-full ${getStatusBadge(selectedFeedback.status).color}`}>
                  {getStatusBadge(selectedFeedback.status).label}
                </span>
                <span className={`px-2 sm:px-3 py-1 text-xs sm:text-sm font-medium rounded-full ${getSeverityBadge(selectedFeedback.severity).color}`}>
                  {getSeverityBadge(selectedFeedback.severity).icon} {getSeverityBadge(selectedFeedback.severity).label}
                </span>
                <span className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                  ID: #{selectedFeedback.id}
                </span>
              </div>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 break-words">
                Da: <span className="font-medium">{selectedFeedback.username || selectedFeedback.name}</span> • 
                Email: <span className="break-all">{selectedFeedback.email}</span>
              </p>
            </div>
          </div>
          
          {/* Azioni Admin - sempre in una riga orizzontale, scroll se necessario */}
          <div className="flex flex-nowrap items-center gap-2 overflow-x-auto pb-1 -mx-1">
            <button
              onClick={() => handleChangeStatus('in_progress')}
              disabled={loading || selectedFeedback.status === 'in_progress'}
              className="flex-shrink-0 px-3 sm:px-4 py-2 text-xs sm:text-sm bg-yellow-100 text-yellow-800 hover:bg-yellow-200 disabled:bg-gray-200 disabled:text-gray-500 rounded-lg transition-colors inline-flex items-center gap-1.5 whitespace-nowrap"
            >
              <Clock className="h-4 w-4 flex-shrink-0" />
              <span>Metti in Lavorazione</span>
            </button>
            
            <button
              onClick={() => handleChangeStatus('closed')}
              disabled={loading || selectedFeedback.status === 'closed'}
              className="flex-shrink-0 px-3 sm:px-4 py-2 text-xs sm:text-sm bg-green-100 text-green-800 hover:bg-green-200 disabled:bg-gray-200 disabled:text-gray-500 rounded-lg transition-colors inline-flex items-center gap-1.5 whitespace-nowrap"
            >
              <CheckSquare className="h-4 w-4 flex-shrink-0" />
              <span>Chiudi Ticket</span>
            </button>
            
            <button
              onClick={() => handleChangeStatus('rejected')}
              disabled={loading || selectedFeedback.status === 'rejected'}
              className="flex-shrink-0 px-3 sm:px-4 py-2 text-xs sm:text-sm bg-red-100 text-red-800 hover:bg-red-200 disabled:bg-gray-200 disabled:text-gray-500 rounded-lg transition-colors inline-flex items-center gap-1.5 whitespace-nowrap"
            >
              <XSquare className="h-4 w-4 flex-shrink-0" />
              <span>Rifiuta</span>
            </button>
            
            {/* Dropdown priorità */}
            <select
              value={selectedFeedback.severity}
              onChange={(e) => handleChangeSeverity(e.target.value)}
              disabled={loading}
              className="flex-shrink-0 px-3 py-2 text-xs sm:text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white min-w-[120px]"
            >
              <option value="low">🟢 Bassa</option>
              <option value="medium">🟡 Media</option>
              <option value="high">🟠 Alta</option>
              <option value="critical">🔴 Critica</option>
            </select>
          </div>
          
          {/* Messaggio originale */}
          <div className="mt-4 p-3 sm:p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <p className="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Messaggio Originale:</p>
            <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300 break-words">{decodeHtmlEntities(selectedFeedback.description)}</p>
          </div>
        </div>

        {/* Conversazione */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 min-h-0">
          {loadingResponses ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner />
            </div>
          ) : responses.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <MessageCircle className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>Nessuna risposta ancora. Sii il primo a rispondere!</p>
            </div>
          ) : (
            responses.map((response) => (
              <div
                key={response.id}
                className={`flex ${response.is_admin_response ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`w-full sm:max-w-2xl ${response.is_admin_response ? 'bg-indigo-50 dark:bg-indigo-900/20' : 'bg-gray-100 dark:bg-gray-700'} rounded-lg p-3 sm:p-4 shadow-sm`}>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 mb-2">
                    <span className={`text-sm sm:text-base font-semibold ${response.is_admin_response ? 'text-indigo-700 dark:text-indigo-300' : 'text-gray-700 dark:text-gray-300'}`}>
                      {response.is_admin_response ? '👨‍💼 Admin' : '🏢 Gym'} - {response.username}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {new Date(response.created_at).toLocaleString('it-IT')}
                    </span>
                  </div>
                  <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words">
                    {decodeHtmlEntities(response.message)}
                  </p>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Input risposta admin */}
        <div className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 p-4 sm:p-6 flex-shrink-0">
          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 rounded-lg flex items-start gap-2">
              <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <span className="text-sm break-words">{error}</span>
            </div>
          )}
          {success && (
            <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200 rounded-lg flex items-start gap-2">
              <CheckCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <span className="text-sm break-words">{success}</span>
            </div>
          )}
          
          <div className="flex flex-col sm:flex-row sm:items-end gap-3">
            <div className="flex-1">
              <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Rispondi alla palestra
              </label>
              <textarea
                value={replyMessage}
                onChange={(e) => setReplyMessage(e.target.value)}
                rows={3}
                className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white resize-none"
                placeholder="Scrivi la tua risposta..."
                disabled={loading}
              />
            </div>
            <button
              onClick={handleSendReply}
              disabled={loading || !replyMessage.trim()}
              className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {loading ? <LoadingSpinner size="sm" /> : <Send className="h-4 w-4" />}
              <span className="text-sm sm:text-base">Invia</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Vista lista feedback (Admin)
  return (
    <div className="h-screen flex flex-col overflow-hidden bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white p-4 sm:p-6 flex-shrink-0">
        <h2 className="text-xl sm:text-2xl font-bold mb-2">
          Gestione Feedback Palestre
        </h2>
        <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-purple-100">
          <span>Totali: {counts.total}</span>
          <span>• Nuovi: <span className="font-bold">{counts.new}</span></span>
          <span>• In Lav.: <span className="font-bold">{counts.in_progress}</span></span>
          <span>• Non Letti: <span className="font-bold text-red-200">{counts.unread}</span></span>
        </div>
      </div>

      {/* Messaggi */}
      {error && (
        <div className="mx-4 sm:mx-6 mt-4 p-3 sm:p-4 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 rounded-lg flex items-start gap-2">
          <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <span className="text-sm break-words">{error}</span>
        </div>
      )}
      {success && (
        <div className="mx-4 sm:mx-6 mt-4 p-3 sm:p-4 bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200 rounded-lg flex items-start gap-2">
          <CheckCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <span className="text-sm break-words">{success}</span>
        </div>
      )}

      {/* Filtri */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-3 sm:p-4 flex-shrink-0">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <Filter className="h-5 w-5 text-gray-400 hidden sm:block flex-shrink-0" />
          
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="w-full sm:w-auto px-2 sm:px-3 py-2 text-xs sm:text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
          >
            <option value="all">Tutti gli Status</option>
            <option value="new">Nuovi ({counts.new})</option>
            <option value="in_progress">In Lavorazione ({counts.in_progress})</option>
            <option value="closed">Chiusi</option>
            <option value="rejected">Rifiutati</option>
          </select>
          
          <select
            value={filterSeverity}
            onChange={(e) => setFilterSeverity(e.target.value)}
            className="w-full sm:w-auto px-2 sm:px-3 py-2 text-xs sm:text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
          >
            <option value="all">Tutte le Priorità</option>
            <option value="critical">🔴 Critica</option>
            <option value="high">🟠 Alta</option>
            <option value="medium">🟡 Media</option>
            <option value="low">🟢 Bassa</option>
          </select>
          
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showOnlyUnread}
              onChange={(e) => setShowOnlyUnread(e.target.checked)}
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
            />
            <span className="text-xs sm:text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
              Solo non letti ({counts.unread})
            </span>
          </label>
        </div>
      </div>

      {/* Tabella feedback */}
      <div className="flex-1 overflow-y-auto p-4 sm:p-6 min-h-0">
        {loading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : filteredFeedbacks.length === 0 ? (
          <div className="text-center py-12">
            <Inbox className="h-12 sm:h-16 w-12 sm:w-16 text-gray-400 mx-auto mb-4" />
            <p className="text-sm sm:text-base text-gray-500 dark:text-gray-400 px-4">
              Nessun feedback trovato con questi filtri
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg shadow">
            <table className="min-w-full bg-white dark:bg-gray-800 rounded-lg overflow-hidden">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Palestra
                  </th>
                  <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Titolo
                  </th>
                  <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden md:table-cell">
                    Tipo
                  </th>
                  <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Priorità
                  </th>
                  <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden lg:table-cell">
                    Risposte
                  </th>
                  <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden sm:table-cell">
                    Data
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredFeedbacks.map((feedback) => (
                  <tr
                    key={feedback.id}
                    onClick={() => handleOpenFeedback(feedback)}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                  >
                    <td className="px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-xs sm:text-sm font-medium text-gray-900 dark:text-white">
                      <span className="hidden sm:inline">#{feedback.id}</span>
                      <span className="sm:hidden">{feedback.id}</span>
                      {feedback.unread_by_admin && (
                        <span className="ml-1 sm:ml-2 inline-block w-2 h-2 bg-red-500 rounded-full"></span>
                      )}
                    </td>
                    <td className="px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-gray-700 dark:text-gray-300 max-w-[100px] sm:max-w-none truncate">
                      {feedback.username || feedback.name || 'N/A'}
                    </td>
                    <td className="px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-gray-900 dark:text-white font-medium max-w-[150px] sm:max-w-none truncate">
                      {decodeHtmlEntities(feedback.title)}
                    </td>
                    <td className="px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-gray-600 dark:text-gray-400 hidden md:table-cell">
                      {feedback.type}
                    </td>
                    <td className="px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap">
                      <span className={`px-1.5 sm:px-2 py-0.5 sm:py-1 text-xs font-medium rounded-full ${getSeverityBadge(feedback.severity).color}`}>
                        {getSeverityBadge(feedback.severity).icon}
                      </span>
                    </td>
                    <td className="px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap">
                      <span className={`px-1.5 sm:px-2 py-0.5 sm:py-1 text-xs font-medium rounded-full ${getStatusBadge(feedback.status).color}`}>
                        <span className="hidden sm:inline">{getStatusBadge(feedback.status).label}</span>
                        <span className="sm:hidden">{getStatusBadge(feedback.status).label.substring(0, 3)}</span>
                      </span>
                    </td>
                    <td className="px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-xs sm:text-sm text-gray-600 dark:text-gray-400 hidden lg:table-cell">
                      <MessageCircle className="h-3 sm:h-4 w-3 sm:w-4 inline mr-1" />
                      {feedback.response_count || 0}
                    </td>
                    <td className="px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-xs sm:text-sm text-gray-500 dark:text-gray-400 hidden sm:table-cell">
                      {new Date(feedback.created_at).toLocaleDateString('it-IT')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

